import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {
  FaBarcode,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaFilePdf,
  FaSearch,
  FaUser,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./BonComptoireList.css";
import "./Factures.css";

import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";

import { invoiceActions } from "../../sagas/invoiceSlice";
import { clientActions } from "../../sagas/clientSlice";
import { productActions } from "../../sagas/productSlice";
import { formatPrice } from "../../helpers/utility";
import { generateDoc } from "../../helpers/generateDoc";
import {
  CBL_PAGE,
  CPMT_INVOICES_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";

const pad = (n) => n.toString().padStart(2, "0");
const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDateString = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};
const formatTouchDate = (value) => {
  const date = parseDateString(value);
  return date ? date.toLocaleDateString("fr-FR") : "--";
};

const Factures = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const searchInputRef = useRef(null);
  const now = useMemo(() => new Date(), []);
  const firstDayOfMonth = useMemo(() => new Date(now.getFullYear(), now.getMonth(), 1), [now]);
  const tomorrowDate = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
    [now]
  );

  const {
    invoices = [],
    loading,
    totals,
    totalPages = 0,
    totalElements = 0,
    pageNumber = 0,
  } = useSelector((state) => state.invoices);
  const clients = useSelector((state) => state.clients.clients || []);
  const unites = useSelector((state) => state.products.unites || []);
  const auth = useSelector((state) => state.auth || {});

  const {
    businessName,
    fullName,
    commercialId,
    pointOfSaleId,
    company,
    address,
    telephone,
    email,
  } = auth;

  const [activeTab, setActiveTab] = useState("INVOICES");
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateField, setDateField] = useState(null);
  const [clientModal, setClientModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState({
    query: "",
    clientId: null,
    dateFrom: formatDate(firstDayOfMonth),
    dateTo: formatDate(tomorrowDate),
  });

  const selectedClient = useMemo(
    () => clients.find((client) => client.id === filters.clientId),
    [clients, filters.clientId]
  );

  const totalTtcSummary = totals?.totalTTC ?? totals?.montantTotalTTC ?? totals?.totalAmountTtc ?? 0;
  const totalRegleSummary = totals?.totalPaye ?? totals?.montantRegle ?? totals?.montantTotalPaye ?? 0;
  const totalRestantSummary = Math.max(totalTtcSummary - totalRegleSummary, 0);

  const fetchInvoices = useCallback(
    (overrides = {}) => {
      const payload = {
        ...filters,
        page: currentPage,
        size: pageSize,
        ...overrides,
      };

      dispatch(invoiceActions.listWithSubInvoices(payload));
      dispatch(
        invoiceActions.totals({
          clientId: payload.clientId,
          dateFrom: payload.dateFrom,
          dateTo: payload.dateTo,
          query: payload.query,
        })
      );
    },
    [dispatch, filters, currentPage, pageSize]
  );

  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
  }, [dispatch]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const clearFilters = () => {
    const nextFilters = {
      query: "",
      clientId: null,
      dateFrom: formatDate(firstDayOfMonth),
      dateTo: formatDate(tomorrowDate),
    };

    setCurrentPage(0);
    setFilters(nextFilters);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchInvoices({ page: 0, query: filters.query });
    setSearchKeypadOpen(false);
  };

  const openDatePicker = (field) => {
    setDateField(field);
    setDatePickerOpen(true);
  };

  const closeDatePicker = () => {
    setDatePickerOpen(false);
    setDateField(null);
  };

  const handleDateChange = (date) => {
    if (!dateField || !date) return;
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, [dateField]: formatDate(date) }));
  };

  const handleGeneratePdf = (invoice) => {
    try {
      generateDoc("facture", {
        matjarDoc: {
          ...invoice,
          lignes: (invoice.lignes || []).map((ligne) => ({
            ...ligne,
            unite: unites.find((unite) => unite.code === ligne.unite)?.libelle || ligne.unite,
          })),
        },
        sellerModel: company || {
          businessName,
          address,
          telephone,
          email,
        },
        priceMode: "HT",
        open: true,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("common.error", "Erreur"),
        text: t("facture.pdfError", "Impossible de générer le PDF."),
      });
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case "BROUILLON":
        return <Badge color="secondary">Brouillon</Badge>;
      case "IMPAYE":
        return <Badge color="secondary">Impayée</Badge>;
      case "VALIDEE":
        return <Badge color="info">Validée</Badge>;
      case "PAYEE":
        return <Badge color="success">Payée</Badge>;
      case "PARTIELLEMENT_PAYEE":
        return <Badge color="warning">Partiellement payée</Badge>;
      case "ANNULEE":
        return <Badge color="danger">Annulée</Badge>;
      case "EN_RETARD":
        return <Badge color="danger">En retard</Badge>;
      default:
        return <Badge color="light" className="text-dark">{statut || "-"}</Badge>;
    }
  };

  const clientLabel = selectedClient
    ? selectedClient.typeClient === "PARTICULIER"
      ? `${selectedClient.nom} ${selectedClient.prenom || ""}`.trim()
      : selectedClient.entreprise
    : "Tous";

  return (
    <Container fluid className="pos-list-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "INVOICES") navigate(CPMT_INVOICES_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
        onCloseShift={clearFilters}
      />

      <Card className="pos-shift-summary shadow-sm border-0">
        <CardBody className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="shift-meta">
            <div className="shift-title">
              <strong>Factures comptoir</strong>
              <Badge color="dark" className="ms-2">{totalElements}</Badge>
            </div>
            <div className="shift-sub text-muted">
              Période : <strong>{filters.dateFrom || "--"}</strong> au <strong>{filters.dateTo || "--"}</strong>
            </div>
          </div>

          <div className="shift-kpis">
            <div className="shift-kpi">
              <small>Total TTC</small>
              <strong>{formatPrice(totalTtcSummary, false)} DH</strong>
            </div>

            <div className="shift-kpi">
              <small>Réglé</small>
              <strong className="text-success">{formatPrice(totalRegleSummary, false)} DH</strong>
            </div>

            <div className="shift-kpi">
              <small>Reste</small>
              <strong className="text-danger">{formatPrice(totalRestantSummary, false)} DH</strong>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="pos-topbar shadow-sm">
        <div className="pos-search">
          <FaSearch className="icon" />
          <Input
            innerRef={searchInputRef}
            value={filters.query}
            placeholder="Rechercher une facture"
            readOnly
            inputMode="none"
            onKeyDown={(e) => e.preventDefault()}
            className="pos-search-input pos-nokeyboard"
            onClick={() => setSearchKeypadOpen(true)}
          />
          <FaBarcode className="barcode" />
        </div>

        <div className="pos-actions">
          <Button className="pos-btn pos-btn-light" color="light" onClick={() => setClientModal(true)}>
            Client : <strong className="ms-2">{clientLabel}</strong>
          </Button>

          <div className="factures-inline-dates">
            <button
              type="button"
              className="factures-touch-date"
              onClick={() => openDatePicker("dateFrom")}
            >
              <span className="factures-touch-date__label">Date début</span>
              <span className="factures-touch-date__value">{formatTouchDate(filters.dateFrom)}</span>
            </button>

            <button
              type="button"
              className="factures-touch-date"
              onClick={() => openDatePicker("dateTo")}
            >
              <span className="factures-touch-date__label">Date fin</span>
              <span className="factures-touch-date__value">{formatTouchDate(filters.dateTo)}</span>
            </button>
          </div>
        </div>
      </div>

      <Card className="pos-list-card shadow-sm border-0">
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t("common.loading", "Chargement...")}</div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-5 text-muted">Aucune facture trouvée.</div>
          ) : (
            <div className="pos-list">
              {invoices.map((invoice) => (
                <div key={invoice.id} className="pos-list-row">
                  <div className="left">
                    <div className="top">
                      <strong className="numero">{invoice.numero}</strong>
                      {getStatusBadge(invoice.statut)}
                    </div>

                    <div className="meta">
                      <span><FaUser className="me-1" /> {invoice.clientNom || "Passager"}</span>
                      <span><FaCalendarAlt className="me-1" /> {invoice.dateFacture || invoice.createdAt || "-"}</span>
                    </div>

                    <div className="amounts">
                      <span className="ttc">{formatPrice(invoice.montantTotalTTC, false)} DH</span>
                      <span className="paye">{formatPrice(invoice.montantRegle ?? invoice.montantPaye ?? 0, false)} DH</span>
                    </div>
                  </div>

                  <div className="right">
                    <Button
                      className="pos-icon-btn pos-pdf"
                      onClick={() => handleGeneratePdf(invoice)}
                      title="Télécharger le PDF"
                    >
                      <FaFilePdf />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <div className="factures-pagination">
        <Button
          color="light"
          className="pos-btn"
          disabled={currentPage <= 0 || loading}
          onClick={() => setCurrentPage((page) => Math.max(page - 1, 0))}
        >
          <FaChevronLeft />
        </Button>
        <span>
          Page <strong>{(pageNumber || 0) + 1}</strong> / <strong>{Math.max(totalPages, 1)}</strong>
        </span>
        <Button
          color="light"
          className="pos-btn"
          disabled={loading || currentPage >= Math.max(totalPages - 1, 0)}
          onClick={() => setCurrentPage((page) => page + 1)}
        >
          <FaChevronRight />
        </Button>
      </div>

      <Modal isOpen={clientModal} toggle={() => setClientModal(false)} centered size="lg">
        <ModalHeader toggle={() => setClientModal(false)}>Choisir Client</ModalHeader>
        <ModalBody className="p-0">
          <div className="pos-picker">
            <Button
              color={!filters.clientId ? "primary" : "light"}
              className="pos-picker-item"
              onClick={() => {
                setCurrentPage(0);
                setFilters((prev) => ({ ...prev, clientId: null }));
                setClientModal(false);
              }}
            >
              Tous
            </Button>

            {clients.map((client) => {
              const label = client.typeClient === "PARTICULIER"
                ? `${client.nom} ${client.prenom || ""}`.trim()
                : client.entreprise;

              return (
                <Button
                  key={client.id}
                  color={filters.clientId === client.id ? "primary" : "light"}
                  className="pos-picker-item"
                  onClick={() => {
                    setCurrentPage(0);
                    setFilters((prev) => ({ ...prev, clientId: client.id }));
                    setClientModal(false);
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </ModalBody>
      </Modal>

      <POSKeypadModal
        isOpen={searchKeypadOpen}
        toggle={() => setSearchKeypadOpen(false)}
        value={filters.query}
        onChange={(value) => setFilters((prev) => ({ ...prev, query: (value || "").toUpperCase() }))}
        onSearch={handleSearch}
        title="Recherche facture"
      />

      <Modal isOpen={datePickerOpen} toggle={closeDatePicker} centered>
        <ModalHeader toggle={closeDatePicker}>
          {dateField === "dateTo" ? "Sélectionner la date fin" : "Sélectionner la date début"}
        </ModalHeader>
        <ModalBody className="factures-date-modal">
          <DatePicker
            inline
            selected={dateField ? parseDateString(filters[dateField]) : null}
            onChange={handleDateChange}
            minDate={dateField === "dateTo" ? parseDateString(filters.dateFrom) || undefined : undefined}
            maxDate={dateField === "dateFrom" ? parseDateString(filters.dateTo) || undefined : undefined}
            calendarClassName="factures-datepicker"
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDatePicker}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Factures;
